import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Building2, Clock, FileText, Languages, MapPin, CheckCircle2, AlertCircle } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import type { EmbassyEntry } from "@/content/embassies/registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema, buildHowToStepsSchema } from "@/lib/schema";
import type { ServicePillarMeta } from "@/content/service-pillars/meta.gen";

/** Real 4-step legalization route for this embassy — shared by head() HowTo and the page body. */
function embassySteps(e: EmbassyEntry): { t: string; d: string }[] {
  return [
    { t: "ปรึกษาและประเมินเอกสาร", d: "ส่งสำเนาเอกสารทาง LINE — ทีมประเมินว่าต้องผ่าน MFA หรือ Apostille, ต้องแปลกี่ภาษา, ใช้เวลากี่วัน" },
    { t: "แปลและรับรอง MoJ", d: `แปลจากไทย → ${e.translationLanguages[0]} (หรือกลับ) โดยนักแปล MoJ ขึ้นทะเบียน — สำหรับ Australia ใช้ NAATI` },
    { t: "รับรอง MFA / Apostille", d: "ส่งกรมการกงสุล กระทรวงการต่างประเทศ — 2 วันด่วน / 4 วันปกติ" },
    { t: `นำเข้าสถานทูต${e.nameTh}`, d: `${e.phuketConsul ? "ใช้กงสุลในภูเก็ตก่อน หากต้องการระดับสูงค่อยส่งกรุงเทพ" : "นำส่งกรุงเทพ"} — เวลา ${e.processingDays}` },
  ];
}

export const Route = createFileRoute("/embassies/$code")({
  loader: async ({
    params,
  }): Promise<{
    embassy: EmbassyEntry;
    relatedPillars: ServicePillarMeta[];
    nextEmbassies: EmbassyEntry[];
  }> => {
    const [{ EMBASSIES, getEmbassyByCode }, { SERVICE_PILLAR_META }] =
      await Promise.all([
        import("@/content/embassies/registry"),
        import("@/content/service-pillars/meta.gen"),
      ]);
    const embassy = getEmbassyByCode(params.code);
    if (!embassy) throw notFound();
    const relatedPillars = (embassy.relatedPillarSlugs ?? [])
      .map((s: string) => SERVICE_PILLAR_META.find((m) => m.slug === s))
      .filter((p): p is ServicePillarMeta => Boolean(p));
    const { pickReciprocal } = await import("@/lib/nearby-links");
    const nextEmbassies = pickReciprocal(
      EMBASSIES.map((x) => ({ ...x, slug: x.code })),
      { ...embassy, slug: embassy.code },
      (x) => x.region,
      6,
    ) as EmbassyEntry[];
    return { embassy, relatedPillars, nextEmbassies };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-3xl font-extrabold">ไม่พบประเทศที่ค้นหา</h1>
        <p className="mt-3 text-muted-foreground">โปรดกลับไปที่ไดเรกทอรีหลัก</p>
        <Link to="/embassies" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground">
          <ArrowLeft className="h-4 w-4" /> กลับไปหน้าไดเรกทอรี
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background p-12 text-center">
      <h1 className="text-2xl font-bold text-destructive">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-muted-foreground">{String(error)}</p>
    </div>
  ),
  component: EmbassyDetail,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const e = loaderData.embassy;
    const site = getActiveSiteUrl();
    const rawTitle = `รับรองเอกสารสถานทูต${e.nameTh} ${e.flag} — Notary+MFA`;
    const title = rawTitle.length > 60 ? rawTitle.slice(0, 59) + "…" : rawTitle;
    const rawDesc = `บริการรับรองเอกสารสถานทูต${e.nameTh} (${e.nameEn}) — ${e.commonDocs.slice(0, 3).join(", ")} เวลา ${e.processingDays} แปลภาษา ${e.translationLanguages.join("/")} ${e.phuketConsul ? "มีกงสุลภูเก็ต" : "ผ่านกรุงเทพ"}.`;
    const description =
      rawDesc.length > 158
        ? rawDesc.slice(0, 157) + "…"
        : rawDesc.length < 120
          ? (rawDesc + " ทีม Notarial Services Attorney จัดเตรียม แปล MFA และนำเข้าสถานทูตให้ครบจบ.").slice(0, 158)
          : rawDesc;
    const base = pageHeadForProvince({
      title,
      description,
      path: `/embassies/${e.code}`,
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/embassies/${e.code}`)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildHowToStepsSchema({
              name: `ขั้นตอนรับรองเอกสารสถานทูต${e.nameTh}`,
              description,
              path: `/embassies/${e.code}`,
              steps: embassySteps(e).map((s) => ({ title: s.t, body: s.d })),
              inLanguage: "th",
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "สถานทูต & กงสุล", url: "/embassies" },
              { name: e.nameTh, url: `/embassies/${e.code}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "GovernmentOffice",
            name: `Embassy of ${e.nameEn} in Thailand`,
            url: `${site}/embassies/${e.code}`,
            address: {
              "@type": "PostalAddress",
              addressLocality: e.embassyCity === "Bangkok" ? "Bangkok" : "Thailand",
              addressCountry: "TH",
            },
            areaServed: { "@type": "Country", name: e.nameEn },
          }),
        },
      ],
    };
  },
});

function EmbassyDetail() {
  const {
    embassy: e,
    relatedPillars,
    nextEmbassies,
  } = Route.useLoaderData() as {
    embassy: EmbassyEntry;
    relatedPillars: ServicePillarMeta[];
    nextEmbassies: EmbassyEntry[];
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <nav className="text-xs text-muted-foreground">
              <Link to="/" className="hover:text-gold">หน้าแรก</Link> ›{" "}
              <Link to="/embassies" className="hover:text-gold">สถานทูต & กงสุล</Link> ›{" "}
              <span className="text-foreground">{e.nameTh}</span>
            </nav>
            <div className="mt-4 flex items-start gap-4">
              <div className="text-5xl">{e.flag}</div>
              <div>
                <h1 className="text-3xl font-extrabold md:text-4xl">
                  รับรองเอกสารสถานทูต{e.nameTh}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">{e.nameEn} — Embassy & Consular Services</p>
              </div>
            </div>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-foreground/90">{e.intro}</p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-border bg-card p-4">
                <MapPin className="h-4 w-4 text-gold" />
                <div className="mt-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">ที่ตั้ง</div>
                <div className="mt-1 text-sm font-semibold">{e.embassyCity === "Bangkok" ? "กรุงเทพ" : "อื่นๆ"}</div>
              </div>
              <div className="rounded-lg border border-border bg-card p-4">
                <Clock className="h-4 w-4 text-gold" />
                <div className="mt-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">เวลาที่ใช้</div>
                <div className="mt-1 text-sm font-semibold">{e.processingDays}</div>
              </div>
              <div className="rounded-lg border border-border bg-card p-4">
                <Languages className="h-4 w-4 text-gold" />
                <div className="mt-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">ภาษาแปล</div>
                <div className="mt-1 text-sm font-semibold">{e.translationLanguages.join(", ")}</div>
              </div>
              <div className={`rounded-lg border p-4 ${e.phuketConsul ? "border-gold bg-gold/5" : "border-border bg-card"}`}>
                <Building2 className="h-4 w-4 text-gold" />
                <div className="mt-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">กงสุลในภูเก็ต</div>
                <div className="mt-1 text-sm font-semibold">{e.phuketConsul ? "✅ มี" : "❌ ไม่มี"}</div>
              </div>
            </div>

            {e.phuketNote && (
              <div className="mt-4 flex gap-3 rounded-lg border-l-4 border-gold bg-card p-4 text-sm">
                <AlertCircle className="h-5 w-5 shrink-0 text-gold" />
                <p className="leading-relaxed text-foreground/90">{e.phuketNote}</p>
              </div>
            )}
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-4 py-12">
          <h2 className="flex items-center gap-2 text-2xl font-extrabold">
            <FileText className="h-6 w-6 text-gold" /> เอกสารที่ทำได้บ่อยที่สุด
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            รายการเอกสารที่สถานทูต{e.nameTh}รับรอง — ทีมเราจัดเตรียม, แปล, ส่ง MFA และนำเข้าสถานทูตให้ครบจบ
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {e.commonDocs.map((d: string) => (
              <li key={d} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span className="leading-relaxed">{d}</span>
              </li>
            ))}
          </ul>

          <h2 className="mt-12 text-2xl font-extrabold">ขั้นตอนการทำงาน (4 Steps)</h2>
          <ol className="mt-6 space-y-4">
            {embassySteps(e).map((s, i) => (
              <li key={i} className="flex gap-4 rounded-lg border border-border bg-card p-5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-base font-bold">{s.t}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>

          {relatedPillars.length > 0 && (
            <>
              <h2 className="mt-12 text-2xl font-extrabold">บริการที่เกี่ยวข้อง</h2>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {relatedPillars.map((p: ServicePillarMeta) => (
                  <a
                    key={p.slug}
                    href={`https://thainotary.co.th/${p.slug}`}
                    target="_blank"
                    rel="noopener"
                    className="group rounded-lg border border-border bg-card p-4 transition hover:border-gold"
                  >
                    <div className="text-sm font-bold group-hover:text-gold">{p.h1}</div>
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{p.heroSubtitle}</p>
                  </a>
                ))}
              </div>
            </>
          )}
        </section>

        {nextEmbassies.length > 0 && (
          <section className="border-t border-border bg-muted/30">
            <div className="mx-auto max-w-4xl px-4 py-10">
              <h2 className="text-lg font-bold">สถานทูตอื่นๆ ในภูมิภาคเดียวกัน</h2>
              <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {nextEmbassies.map((n) => (
                  <Link
                    key={n.code}
                    to="/embassies/$code"
                    params={{ code: n.code }}
                    className="rounded-lg border border-border bg-card px-3 py-2 text-sm hover:border-gold hover:text-gold"
                  >
                    {n.flag} {n.nameTh}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">เริ่มต้นทำเอกสารสำหรับ{e.nameTh}</h2>
            <p className="mt-3 text-sm text-primary-foreground/90">
              เริ่ม 2,500 บาท/รอบ — รวมแปล, MFA, และนำส่งสถานทูต
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-3 text-sm font-bold text-primary hover:bg-gold/90"
            >
              ปรึกษาฟรี — แจ้งประเภทเอกสารและกำหนดส่ง
            </Link>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="consular" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}